// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Stack, useLocalSearchParams } from 'expo-router';
import { useEffect, useMemo } from 'react';
import { ActivityIndicator, StyleSheet } from 'react-native';

import { KEYCLOAK_BASE } from '@/auth/oidc';
import { Text, View } from '@/components/Themed';
import { t } from '@/i18n';

/**
 * Short entry point for the Wear OS device-code sign-in: `goffy.app/device?user_code=ABCD-EFGH`.
 *
 * Keycloak's own device URL is far too long to read off a 203dp watch face and
 * type on a phone, so the watch shows this one and we forward to Keycloak here.
 * It lives in the web app because goffy.app is the SPA distribution — Envoy, which
 * has the equivalent redirect, only serves backend.goffy.app.
 */
export default function DeviceLinkScreen() {
  const params = useLocalSearchParams<{ user_code?: string; userCode?: string }>();
  const userCode = (params.user_code ?? params.userCode ?? '').toString().trim();

  // KEYCLOAK_BASE, not the API base: they are the same host in production only
  // because Envoy serves the realm under it, and differ everywhere else.
  const target = useMemo(() => {
    const device = `${KEYCLOAK_BASE}/device`;
    return userCode ? `${device}?user_code=${encodeURIComponent(userCode)}` : device;
  }, [userCode]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // replace(), not assign(): Back from Keycloak should return to whatever the
    // user was on before, not bounce them through this redirect again.
    window.location.replace(target);
  }, [target]);

  return (
    <>
      <Stack.Screen options={{ title: t('deviceLink.title') }} />
      <View style={styles.container}>
        <ActivityIndicator />
        <Text style={styles.message}>{t('deviceLink.message')}</Text>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: 20,
  },
  message: {
    fontSize: 15,
    textAlign: 'center',
  },
});
